/**
 * Groups the "Set as Graphic Standard" rows by the element they style
 * (PLAT-11993), so an element's own row — its text or image — heads a block
 * with its Color / Font / Font Size settings under it, instead of every
 * control in one flat list.
 *
 * Pure: takes the rows `buildStandardsDiffRows` produced and the template
 * source (for element order and names) and returns ordered groups.
 *
 * - One group per element a control targets, in the element's order in the
 *   source (depth first), so the modal reads top to bottom like the card.
 * - A control that targets several elements at once (an unnormalized shared
 *   Font control) goes to a trailing "Shared" group: it belongs to none of
 *   them alone.
 * - Rows whose element is not in the source keep their relative order after
 *   the source-ordered groups.
 */

import type { Source } from '@src/components/sidepanel/CompositionSettings/types';
import type { ControlDiffRow } from '@src/utility/compositionSettings';

export type StandardsGroupKind = 'element' | 'shared';

export interface StandardsGroup {
  /** Stable key: the element's path, or 'shared'. */
  key: string;
  kind: StandardsGroupKind;
  /** The element's path from the template root, for element groups. */
  path?: number[];
  /** The element's name in the source, or a fallback for display. */
  title: string;
  /**
   * The row that IS the element (its `text` or `source` control): it heads
   * the group and its label is the element's name. Absent when the element
   * has only settings.
   */
  primary?: ControlDiffRow;
  /** Every other row on the element, in their original order. */
  settings: ControlDiffRow[];
}

export const pathKey = (path: number[]) => path.join('.');

const isPrimary = (row: ControlDiffRow) =>
  row.control.property === 'text' || row.control.property === 'source';

/** Element paths in the source, depth first, with each element's name. */
export function enumerateSourceElements(
  source: Pick<Source, 'elements'> | null | undefined,
): { path: number[]; name?: string }[] {
  const out: { path: number[]; name?: string }[] = [];
  const walk = (elements: unknown, prefix: number[]) => {
    if (!Array.isArray(elements)) return;
    elements.forEach((el: unknown, i) => {
      if (!el || typeof el !== 'object') return;
      const path = [...prefix, i];
      const name = 'name' in el ? el.name : undefined;
      out.push({
        path,
        name: typeof name === 'string' && name.trim() ? name.trim() : undefined,
      });
      if ('elements' in el) walk(el.elements, path);
    });
  };
  walk(source?.elements, []);
  return out;
}

export function groupStandardsRows(
  rows: ControlDiffRow[],
  source?: Pick<Source, 'elements'> | null,
  /**
   * Elements that get a block even without a control row — e.g. every element
   * with a Position / Size readout (PLAT-11993).
   */
  extraPaths: number[][] = [],
): StandardsGroup[] {
  const elements = enumerateSourceElements(source);
  const order = new Map(elements.map((e, i) => [pathKey(e.path), i]));
  const nameByKey = new Map(elements.map(e => [pathKey(e.path), e.name]));

  const byKey = new Map<string, StandardsGroup>();
  const firstSeen = new Map<string, number>();
  const shared: ControlDiffRow[] = [];

  rows.forEach((row, index) => {
    const paths = row.control.elementPaths ?? [];
    if (paths.length !== 1) {
      shared.push(row);
      return;
    }
    const path = paths[0];
    const key = pathKey(path);
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        kind: 'element',
        path,
        title: nameByKey.get(key) ?? '',
        settings: [],
      };
      byKey.set(key, group);
      firstSeen.set(key, index);
    }
    if (isPrimary(row) && !group.primary) group.primary = row;
    else group.settings.push(row);
  });

  extraPaths.forEach(path => {
    const key = pathKey(path);
    if (byKey.has(key)) return;
    byKey.set(key, {
      key,
      kind: 'element',
      path,
      title: nameByKey.get(key) ?? '',
      settings: [],
    });
    firstSeen.set(key, rows.length + firstSeen.size);
  });

  const groups = Array.from(byKey.values()).map(group => ({
    ...group,
    title:
      group.title ||
      group.primary?.label ||
      group.settings[0]?.label ||
      'Element',
  }));

  // Source order first; elements the source doesn't list keep the order their
  // first row had.
  groups.sort((a, b) => {
    const oa = order.get(a.key);
    const ob = order.get(b.key);
    if (oa !== undefined && ob !== undefined) return oa - ob;
    if (oa !== undefined) return -1;
    if (ob !== undefined) return 1;
    return (firstSeen.get(a.key) ?? 0) - (firstSeen.get(b.key) ?? 0);
  });

  if (shared.length > 0) {
    groups.push({
      key: 'shared',
      kind: 'shared',
      title: 'Shared',
      settings: shared,
    });
  }
  return groups;
}
